import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Globe, CheckCircle2, MessageSquareWarning, FileText, Palette, Link2, Image as ImageIcon } from 'lucide-react';
import { LinkCard } from '../components/website-vision/LinkList';
import { loadGoogleFonts, readableOn } from '../components/website-vision/brandData';
import { ApprovalBadge } from '../components/website-vision/HandoffShareBar';
import { colorName } from '../components/website-vision/colorNames';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3"><Icon size={16} className="text-[#1D4ED8]" /><h2 className="font-bold text-base text-[#0A0F1C]">{title}</h2></div>
      {children}
    </section>
  );
}

function ReviewForm({ token, onDone }) {
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState('');
  const submit = async (decision) => {
    setBusy(decision);
    try {
      const r = await axios.post(`${BACKEND}/api/website-vision/share/${token}/review`, { decision, name, comment });
      onDone(r.data.approval);
      toast.success(decision === 'approved' ? 'Approved — thank you!' : 'Thanks! Your feedback was sent.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit'); }
    finally { setBusy(''); }
  };
  return (
    <section className="bg-[#0A0F1C] text-white rounded-2xl p-5" data-testid="review-form">
      <h2 className="font-bold text-lg mb-1">Does this capture your vision?</h2>
      <p className="text-sm text-slate-300 mb-4">Approve it so we can start building, or tell us what you'd like changed.</p>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="review-name"
        className="w-full mb-2 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm placeholder:text-slate-400 outline-none focus:border-[#F97316]" />
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Comments or requested changes (required if requesting changes)" data-testid="review-comment"
        className="w-full mb-3 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm placeholder:text-slate-400 outline-none focus:border-[#F97316]" />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => submit('approved')} disabled={!!busy} data-testid="review-approve-btn"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold text-sm disabled:opacity-60 transition-colors">
          {busy === 'approved' ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Approve vision
        </button>
        <button onClick={() => submit('changes_requested')} disabled={!!busy} data-testid="review-changes-btn"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 font-bold text-sm disabled:opacity-60 transition-colors">
          {busy === 'changes_requested' ? <Loader2 size={15} className="animate-spin" /> : <MessageSquareWarning size={15} />} Request changes
        </button>
      </div>
    </section>
  );
}

export default function VisionSharePage() {
  const { token } = useParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    loadGoogleFonts();
    axios.get(`${BACKEND}/api/website-vision/share/${token}`).then((r) => setV(r.data)).catch((e) => setErr(e.response?.data?.detail || 'Not found'));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-[#475569]" data-testid="share-error">{err}</div>;
  if (!v) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#1D4ED8]" /></div>;

  const brand = v.brand || {};
  const images = (v.assets || []).filter((a) => a.kind !== 'document');
  const docs = (v.assets || []).filter((a) => a.kind === 'document');
  const links = [...(v.inspiration_sites || []), ...(v.other_links || [])];
  const fields = Object.entries(v.fields || {}).filter(([k, val]) => k !== 'complete_vision' && String(val || '').trim());
  const fullVision = (v.fields?.complete_vision || '').trim();

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="vision-share-page">
      <header className="bg-[#0A0F1C] text-white">
        <div className="max-w-4xl mx-auto px-5 py-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F97316] mb-2">DCA Enterprises · Website Vision Review</div>
          <h1 className="text-4xl sm:text-5xl font-black leading-tight" data-testid="share-title">{v.title}</h1>
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-300">
            <Globe size={15} /> {v.client_name}{v.site_type ? ` · ${v.site_type}` : ''} <ApprovalBadge approval={v.approval} />
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 py-8 space-y-5">
        {fullVision && (
          <Section icon={Globe} title="The complete vision" testid="share-complete-vision">
            <p className="text-sm leading-relaxed text-[#1e293b] whitespace-pre-wrap">{fullVision}</p>
          </Section>
        )}
        {(brand.palette?.length > 0 || brand.heading_font) && (
          <Section icon={Palette} title="Brand look" testid="share-brand">
            <div className="flex flex-wrap gap-3 mb-4">
              {(brand.palette || []).map((c) => (
                <div key={c.hex} className="text-center">
                  <div className="w-16 h-16 rounded-xl border border-black/10 flex items-end justify-center pb-1 text-[10px] font-mono" style={{ background: c.hex, color: readableOn(c.hex) }}>{c.hex}</div>
                  <div className="text-[11px] font-semibold text-[#0A0F1C] mt-1">{colorName(c.hex)}</div>
                  <div className="text-[10px] text-[#64748B]">{c.label}</div>
                </div>
              ))}
            </div>
            {brand.heading_font && <div className="text-3xl text-[#0A0F1C]" style={{ fontFamily: brand.heading_font, fontWeight: 700 }}>{brand.heading_font} headline</div>}
            {brand.body_font && <p className="text-sm text-[#475569] mt-1" style={{ fontFamily: brand.body_font }}>Body text set in {brand.body_font}.</p>}
          </Section>
        )}
        {images.length > 0 && (
          <Section icon={ImageIcon} title="Images" testid="share-images">
            <div className="flex flex-wrap gap-3">
              {images.map((a) => <a key={a.asset_id} href={`${BACKEND}${a.url}`} target="_blank" rel="noopener noreferrer"><img src={`${BACKEND}${a.url}`} alt={a.filename} className="w-24 h-24 object-contain bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]" /></a>)}
            </div>
          </Section>
        )}
        {(links.length > 0 || docs.length > 0) && (
          <Section icon={Link2} title="Links & documents" testid="share-links">
            <div className="grid sm:grid-cols-2 gap-2">
              {links.map((l, i) => <LinkCard key={i} link={l} testid={`share-link-${i}`} />)}
              {docs.map((a) => (
                <a key={a.asset_id} href={`${BACKEND}${a.url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-3 py-2.5 hover:border-[#1D4ED8] text-sm font-semibold text-[#0A0F1C]"><FileText size={16} className="text-[#1D4ED8]" /> <span className="truncate">{a.filename}</span></a>
              ))}
            </div>
          </Section>
        )}
        {v.build_brief?.markdown ? (
          <Section icon={FileText} title="Build Brief" testid="share-brief">
            <pre className="whitespace-pre-wrap text-sm leading-relaxed text-[#1e293b] font-sans">{v.build_brief.markdown}</pre>
          </Section>
        ) : fields.length > 0 && (
          <Section icon={FileText} title="Your answers" testid="share-answers">
            <dl className="grid sm:grid-cols-2 gap-3">{fields.map(([k, val]) => <div key={k}><dt className="text-xs font-bold uppercase text-[#64748B]">{k.replace(/_/g, ' ')}</dt><dd className="text-sm text-[#0A0F1C]">{String(val)}</dd></div>)}</dl>
          </Section>
        )}
        <ReviewForm token={token} onDone={(ap) => setV({ ...v, approval: ap })} />
      </main>
    </div>
  );
}
